import { ResponseResolver, restContext, RestRequest } from 'msw';
import { delayedResponse } from '@/utils/useMswCustomResponse';
import { choiceRandom } from '@/utils/useHelper';
import { MonthlyParkingResponse } from '../types/monthlyParking';
import {
  Notice,
  NoticeDetailResponse,
  NoticesResponse,
  NoticeContentData,
} from '../types/notice';
import {
  MONTHLY_NOTICE_MESSAGES,
  MONTHLY_NOTICE_TYPE,
} from '../../constants/notice';
import mockApplicant from './applicantResolver';
import mockApplication from './applicationResolver';
import mockPreApplication from './preApplicationResolver';
import mockResubmission from './resubmissionResolver';
import mockMonthlyPayment from './monthlyPaymentResolver';

/**
 * 月極駐車場詳細を取得
 */
const getMonthlyParking: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const parkingId = Number(req.params.parkingId) || 1;

  // テストケースに対応したデータ生成
  const getMonthlyTestData = (id: number) => {
    switch (id) {
      case 4: // 月極: 貸出区画がない駐車場
        return {
          hasAvailableGarage: false,
          canAcceptApplications: false,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】月極:貸出区画なし',
        };
      case 5: // 月極: 全ての貸出区画が公開日前の駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: false,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】月極:全区画公開日前',
        };
      case 6: // 月極: 公開日を過ぎた貸出区画がひとつでもある駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】月極:仮申込可能',
        };
      case 7: // 詳細: オープン前の駐車場（公開予定画面表示）
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】詳細:オープン前（公開予定）',
        };
      case 8: // 詳細: オープン済み駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】詳細:オープン済み',
        };
      case 9: // 詳細: 時間貸しできない駐車場
        return {
          hasAvailableGarage: false,
          canAcceptApplications: false,
          isOpened: true,
          publishDateTime: '2024-01-01T09:00:00+09:00',
          name: '【テスト】詳細:時間貸しできない',
        };
      case 10: // 月極詳細: 貸出区画がない駐車場
        return {
          hasAvailableGarage: false,
          canAcceptApplications: false,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極詳細:契約できない',
        };
      case 11: // 月極詳細: 全ての貸出区画が公開日前の駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: false,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極詳細:全区画公開日前',
        };
      case 12: // 月極詳細: 公開日を過ぎた貸出区画がひとつでもある駐車場
        return {
          hasAvailableGarage: true,
          canAcceptApplications: true,
          isOpened: false,
          publishDateTime: '2025-12-31T09:00:00+09:00',
          name: '【テスト】月極詳細:仮申込可能',
        };
      default:
        return {
          hasAvailableGarage: !!choiceRandom([0, 1]),
          canAcceptApplications: parkingId !== 31,
          isOpened: parkingId % 3 !== 0,
          publishDateTime:
            parkingId % 3 === 0
              ? '2025-12-31T09:00:00+09:00'
              : '2024-01-01T09:00:00+09:00',
          name: `テスト駐車場 ${parkingId}`,
        };
    }
  };

  const testData = getMonthlyTestData(parkingId);
  const hasAvailableGarage = testData.hasAvailableGarage;
  const canAcceptApplications = testData.canAcceptApplications;

  // 貸出区画リストを生成
  const rentalGarages = hasAvailableGarage
    ? [
        {
          contractAmount: 10000,
          contractAmountWithTax: 11000,
          depositAmount: 30000,
          monthlyAmount: 20000,
          monthlyAmountWithTax: 22000,
          type: '普通',
          content: '普通車用',
          isDummy: choiceRandom([true, false]),
        },
        {
          contractAmount: 8000,
          contractAmountWithTax: 8800,
          depositAmount: 25000,
          monthlyAmount: 15000,
          monthlyAmountWithTax: 16500,
          type: 'バイク',
          content: null,
          isDummy: false,
        },
        {
          contractAmount: 12000,
          contractAmountWithTax: 13200,
          depositAmount: 35000,
          monthlyAmount: 25000,
          monthlyAmountWithTax: 27500,
          type: '特殊車両',
          content: '大型',
          isDummy: false,
        },
        {
          contractAmount: 12000,
          contractAmountWithTax: 13200,
          depositAmount: 35000,
          monthlyAmount: 25000,
          monthlyAmountWithTax: 27500,
          type: '普通',
          content: '普通車用',
          isDummy: false,
        },
      ]
    : [];

  // 貸出区画の集計を計算
  const rentalGaragesSummary: MonthlyParkingResponse['rentalGaragesSummary'] = {
    maxContractAmount: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.contractAmount))
      : 0,
    maxContractAmountWithTax: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.contractAmountWithTax))
      : 0,
    maxDepositAmount: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.depositAmount))
      : 0,
    maxMonthlyAmount: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.monthlyAmount))
      : 0,
    maxMonthlyAmountWithTax: hasAvailableGarage
      ? Math.max(...rentalGarages.map((garage) => garage.monthlyAmountWithTax))
      : 0,
    minContractAmount: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.contractAmount))
      : 0,
    minContractAmountWithTax: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.contractAmountWithTax))
      : 0,
    minDepositAmount: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.depositAmount))
      : 0,
    minMonthlyAmount: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.monthlyAmount))
      : 0,
    minMonthlyAmountWithTax: hasAvailableGarage
      ? Math.min(...rentalGarages.map((garage) => garage.monthlyAmountWithTax))
      : 0,
  };

  return delayedResponse(
    ctx.status(200),
    ctx.json<MonthlyParkingResponse>({
      garageImageUrl: 'https://picsum.photos/700/500',
      hasApplied: Math.random() > 0.5, // ランダムに申込済みフラグを設定
      openTime: '24時間',
      parkingAddress: '東京都渋谷区道玄坂1-2-3',
      parkingId,
      parkingName: testData.name,
      // テストケース対応
      isOpened: testData.isOpened,
      publishDateTime: testData.publishDateTime,
      hasAvailableGarage,
      rentalGarages,
      rentalGaragesSummary,
      parkingLatitude: 35.710063,
      parkingLongitude: 139.8107,
      isDummy: rentalGarages.some((garage) => garage.isDummy),
      canAcceptApplications,
      applicationSuspensionPeriod: canAcceptApplications
        ? null
        : {
            startDate: '2025-01-01',
            endDate: '2025-01-31',
            resumeDate: '2025-02-01',
            reason:
              '誠に勝手ながら夏期休業に伴い仮申込の受付を一時停止しております。10月1日より受付再開いたします。',
          },
    }),
  );
};

// ログインユーザー宛の通知一覧を取得
const getNotices: ResponseResolver<RestRequest, typeof restContext> = async (
  req,
  res,
  ctx,
) => {
  const pinned = req.url.searchParams.get('pinned');
  const viewed = req.url.searchParams.get('viewed');
  const page = Number(req.url.searchParams.get('page')) || 1;
  const limit = Number(req.url.searchParams.get('limit')) || 1;

  const totalItems = MONTHLY_NOTICE_MESSAGES.length;
  const totalPages = Math.ceil(totalItems / limit);

  const generatedNotices = MONTHLY_NOTICE_MESSAGES.map<Notice>(
    (message, index) => ({
      id: index,
      type: message.TYPE,
      isPinned: true,
      publishedAt: new Date().toString(),
      viewedAt: choiceRandom([null, new Date().toString()]),
    }),
  );

  return delayedResponse(
    ctx.status(200),
    ctx.json<NoticesResponse>({
      notices: generatedNotices.slice(
        limit * (page - 1),
        limit * (page - 1) + limit,
      ),
      pagination: {
        total: totalItems,
        perPage: limit,
        currentPage: page,
        lastPage: totalPages,
        firstPageUrl: '',
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
      },
    }),
  );
};

/**
 * 通知詳細を取得する
 */
const getNoticeDetail: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  const noticeId = req.params.noticeId;
  const noticeType = MONTHLY_NOTICE_MESSAGES[Number(noticeId)].TYPE;

  // 全ての通知で共通のデータを生成
  const contentData: NoticeContentData = {
    parkingId: 5678,
    parkingName: '渋谷パーキング',
    applicationId:
      noticeType === MONTHLY_NOTICE_TYPE.UNSUBMITTED_IMAGE_SUBMISSION_REQUEST
        ? 8
        : 17,
    billingAmount: 22000,
    billingCardNumber: '************1234',
    billingDueDate: '2025-05-31T23:59:59',
    billingId: 9876,
    billingTargetMonth: '2025-05-15T14:30:45',
    cancellationRequestedAt: '2025-05-15T14:30:45',
    cancellationRequestId: 5678,
    contractCloseDate: '2025-06-30',
    contractUpdatedDate: '2025-06-01',
    previousGarageNumber: 105,
    previousMonthlyAmount: 22000,
    contractEndDate: '2025-08-31',
    contractRenewalDueDate: '2025-06-30',
    contractGarageNumber: 201,
    contractMonthlyAmount: 25000,
    contractRenewalAmount: 10000,
    unsubmittedImageNames: ['車検証', '運転免許証裏面', '車両側面写真'],
    billingPaidAt: '2025-05-20T10:15:30',
    contractRenewalAmountWithTax: 2000,
    notSubmittedImages: [],
    nextContractMonthlyAmount: 55_000,
    nextContractAppliedFrom: '2024-01-01T00:00:00+09:00',
  };

  return delayedResponse(
    ctx.status(200),
    ctx.json<NoticeDetailResponse>({
      id: Number(noticeId),
      type: noticeType,
      isPinned: true,
      publishedAt: new Date().toString(),
      viewedAt: null,
      data: contentData,
    }),
  );
};

// 貸出の通知を閲覧済みにする
const markNoticeAsViewed: ResponseResolver<
  RestRequest,
  typeof restContext
> = async (req, res, ctx) => {
  return delayedResponse(ctx.status(204));
};

const mockMonthly = {
  // 駐車場関連
  getMonthlyParking,
  // 通知関連
  getNotices,
  getNoticeDetail,
  markNoticeAsViewed,
  ...mockApplicant,
  ...mockApplication,
  ...mockPreApplication,
  ...mockResubmission,
  ...mockMonthlyPayment,
};

export default mockMonthly;
